import React, { useState, useEffect } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Toolbar, Box, IconButton, Tooltip } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon2 from '@mui/icons-material/AccountBalance';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InventoryIcon from '@mui/icons-material/Inventory';

// Navigation link type
interface NavLink {
  to: string;
  label: string;
  icon: React.ReactElement;
  tabIndex?: number;
}

// Client navigation links
const clientNavLinks: NavLink[] = [
  { to: '/home', label: 'Dashboard', icon: <DashboardIcon /> },
  { to: '/loan-application', label: 'Loan Applications', icon: <AddBusinessIcon /> },
  { to: '/accounts', label: 'Loan Accounts', icon: <AccountBalanceIcon /> },
  { to: '/savings', label: 'Savings Accounts', icon: <AccountBalanceWalletIcon /> },
  { to: '/transactions', label: 'Transactions', icon: <ReceiptIcon /> },
  { to: '/statements', label: 'Statements', icon: <DescriptionIcon /> },
  { to: '/payments', label: 'Payments', icon: <PaymentIcon /> },
  { to: '/survey', label: 'Survey', icon: <AssignmentIcon /> },
];

// Base admin navigation links (without Operations which needs dynamic index)
const baseAdminNavLinks: NavLink[] = [
  { to: '/admin', label: 'Products', icon: <InventoryIcon />, tabIndex: 1 },
  { to: '/admin', label: 'Payment Management', icon: <PaymentIcon />, tabIndex: 2 },
  { to: '/admin', label: 'ZIG Rate Management', icon: <CurrencyExchangeIcon />, tabIndex: 3 },
  { to: '/admin', label: 'Client Management', icon: <PeopleIcon />, tabIndex: 4 },
  { to: '/admin', label: 'Metrics & Analytics', icon: <AssessmentIcon />, tabIndex: 5 },
];

// Helper function to get admin nav links with correct Operations tab index
const getAdminNavLinks = (): NavLink[] => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return [
        ...baseAdminNavLinks,
        { to: '/admin', label: 'Operations', icon: <ScheduleIcon />, tabIndex: 6 },
      ];
    }
    
    const { jwtDecode } = require('jwt-decode');
    const decoded: any = jwtDecode(token);
    
    const realmRoles = decoded.realm_access?.roles || [];
    const resourceRoles: string[] = [];
    
    if (decoded.resource_access) {
      Object.values(decoded.resource_access).forEach((client: any) => {
        if (client.roles) {
          resourceRoles.push(...client.roles);
        }
      });
    }
    
    const allRoles = [...realmRoles, ...resourceRoles];
    const canViewAuditLogs = allRoles.some((role: string) => {
      const upperRole = role.toUpperCase();
      return upperRole === 'ADMINISTRATOR' || 
             upperRole === 'ROLE_ADMINISTRATOR' ||
             upperRole === 'AUDIT' ||
             upperRole === 'ROLE_AUDIT';
    });
    
    // Operations is at tab 7 when Audit Logs is visible, tab 6 when hidden
    return [
      ...baseAdminNavLinks,
      { to: '/admin', label: 'Operations', icon: <ScheduleIcon />, tabIndex: canViewAuditLogs ? 7 : 6 },
    ];
  } catch (error) {
    return [
      ...baseAdminNavLinks,
      { to: '/admin', label: 'Operations', icon: <ScheduleIcon />, tabIndex: 6 },
    ];
  }
};

// Helper function to check if user is admin (any admin role)
const isAdmin = (): boolean => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    const { jwtDecode } = require('jwt-decode');
    const decoded: any = jwtDecode(token);
    
    const realmRoles = decoded.realm_access?.roles || [];
    const resourceRoles: string[] = [];
    
    if (decoded.resource_access) {
      Object.values(decoded.resource_access).forEach((client: any) => {
        if (client.roles) {
          resourceRoles.push(...client.roles);
        }
      });
    }
    
    const allRoles = [...realmRoles, ...resourceRoles];
    
    // Define all admin roles that should have access to admin dashboard
    const adminRoles = [
      'ADMINISTRATOR', 'ROLE_ADMINISTRATOR',
      'ACCOUNTING_ASSISTANT', 'ROLE_ACCOUNTING_ASSISTANT',
      'ASSISTANT_ACCOUNTANT', 'ROLE_ASSISTANT_ACCOUNTANT',
      'CEO', 'ROLE_CEO',
      'FINANCE_MANAGER', 'ROLE_FINANCE_MANAGER',
      'OPERATIONS_MANAGER', 'ROLE_OPERATIONS_MANAGER',
      'OPERATIONS', 'ROLE_OPERATIONS',
      'FINANCE_DIRECTOR', 'ROLE_FINANCE_DIRECTOR',
      'OPERATIONS_DIRECTOR', 'ROLE_OPERATIONS_DIRECTOR',
      'RISK', 'ROLE_RISK',
      'AUDITOR', 'ROLE_AUDITOR',
      'AUDIT', 'ROLE_AUDIT'
    ];
    
    return allRoles.some((role: string) => 
      adminRoles.includes(role.toUpperCase())
    );
  } catch (error) {
    return false;
  }
};

// Helper function to check if user is ADMINISTRATOR (only role that can see audit logs)
const isAdministrator = (): boolean => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    const { jwtDecode } = require('jwt-decode');
    const decoded: any = jwtDecode(token);
    
    const realmRoles = decoded.realm_access?.roles || [];
    const resourceRoles: string[] = [];
    
    if (decoded.resource_access) {
      Object.values(decoded.resource_access).forEach((client: any) => {
        if (client.roles) {
          resourceRoles.push(...client.roles);
        }
      });
    }
    
    const allRoles = [...realmRoles, ...resourceRoles];
    return allRoles.some((role: string) => 
      role.toUpperCase() === 'ADMINISTRATOR' || role.toUpperCase() === 'ROLE_ADMINISTRATOR'
    );
  } catch (error) {
    return false;
  }
};

const expandedWidth = 220;
const collapsedWidth = 64;

const LeftSidebar: React.FC = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    setIsAdminUser(isAdmin());
  }, [location.pathname]);

  // Hide sidebar on login and register pages
  if (location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  // Determine which links to show
  const navLinks = isAdminUser ? getAdminNavLinks() : clientNavLinks;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? collapsedWidth : expandedWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        [`& .MuiDrawer-paper`]: {
          width: collapsed ? collapsedWidth : expandedWidth,
          transition: 'width 0.2s',
          boxSizing: 'border-box',
          background: '#002147',
          color: '#fff',
          borderRight: 'none',
          boxShadow: 'none',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}
    >
      <Box>
        <Toolbar sx={{ minHeight: 48 }} />
        <Box sx={{ overflowY: 'auto', overflowX: 'hidden', mt: 2 }}>
          <List>
            {navLinks.map((link, index) => {
              const selected = isAdminUser && link.tabIndex !== undefined
                ? location.pathname === '/admin'
                : location.pathname.startsWith(link.to);
              
              const handleClick = (e: React.MouseEvent) => {
                // Check authentication before navigation
                if (!isAuthenticated()) {
                  e.preventDefault();
                  sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
                  window.location.href = '/login';
                  return;
                }
                
                // For admin links with tabIndex, navigate to admin and set tab
                if (isAdminUser && link.tabIndex !== undefined) {
                  e.preventDefault();
                  // Store the tab index in sessionStorage for AdminDashboard to read
                  sessionStorage.setItem('adminTabIndex', link.tabIndex!.toString());
                  window.location.href = '/admin';
                }
              };
              
              return (
                <Tooltip key={link.to + index} title={collapsed ? link.label : ''} placement="right">
                  <ListItem
                    button
                    component={isAdminUser && link.tabIndex !== undefined ? 'div' : Link}
                    {...(isAdminUser && link.tabIndex !== undefined ? {} : { to: link.to })}
                    selected={selected}
                    onClick={handleClick}
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      mx: 1,
                      background: 'transparent',
                      color: '#fff',
                      fontWeight: selected ? 700 : 400,
                      '&:hover': {
                        background: !selected ? 'rgba(255,255,255,0.08)' : 'transparent',
                        color: '#fff',
                      },
                      minHeight: 48,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      px: collapsed ? 0 : 2,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 2, justifyContent: 'center', color: '#fff' }}>
                      {link.icon}
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary={link.label} />}
                  </ListItem>
                </Tooltip>
              );
            })}
          </List>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', p: 1 }}>
        <IconButton
          onClick={() => {
            // Check authentication before collapsing/expanding
            if (!isAuthenticated()) {

              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
              return;
            }
            setCollapsed((prev) => !prev);
          }}
          sx={{ color: '#fff', border: '1.5px solid #fff', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' } }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
    </Drawer>
  );
};

export default LeftSidebar; 
